import React from 'react';
import ConstraintsTable from './ConstraintsTable';
import ConstraintSelector from './ConstraintSelector';
import type { ConstraintTableColumn } from '../../../../config/optimizePlanConfig';
import {
    TPO_OPTIMIZE_CONSTRAINTS_ERROR,
    TPO_OPTIMIZE_CONSTRAINTS_TABLE,
    tpoPartTestId,
} from '../../../../config/testIds';
import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import type { ConstraintGroup, ConstraintInputValue, Level } from './types';

interface ConstraintsTabProps {
    groups: ConstraintGroup[];
    tableHeaders: ConstraintTableColumn[];
    kpiList: { name: string; label: string }[];
    selectedConstraintNames: string[];
    onKPISelectHandler: (selectedNames: string[]) => void;
    onAddLevel: (constraintName: string, level: Level) => void;
    onRemoveLevel: (constraintName: string, level: Level) => void;
    onRemoveAttribute: (constraintName: string, level: Level, attributeName: string) => void;
    onInputChange: (
        constraintName: string,
        level: Level,
        attributeName: string,
        patch: Partial<ConstraintInputValue>,
        fallback: ConstraintInputValue,
    ) => void;
    errorExists: boolean;
    /** True while the one request that carries both the catalog and the values is in flight. */
    isLoading: boolean;
    /** Blocks the selector while the objective/date that scope the fetch are unset. */
    selectorDisabled: boolean;
}

const ConstraintsTab: React.FC<ConstraintsTabProps> = ({
    groups,
    tableHeaders,
    kpiList,
    selectedConstraintNames,
    onKPISelectHandler,
    onAddLevel,
    onRemoveLevel,
    onRemoveAttribute,
    onInputChange,
    errorExists,
    isLoading,
    selectorDisabled,
}) => {
    const { constraintsSection, tableError, emptyConstraints, noConstraintsSelected, level, statusMessages } =
        useTpoGenConfig().labels.optimizePlan;

    // The catalog arrives with the values, so there is nothing to offer until the
    // request lands — and the empty state has to read as "not ready yet" rather
    // than "pick something".
    const cannotSelect = selectorDisabled || isLoading || kpiList.length === 0;

    const emptyMessage = isLoading
        ? statusMessages.loadingConstraints
        : cannotSelect
          ? emptyConstraints
          : noConstraintsSelected;

    return (
        <div className="h-full flex flex-col overflow-hidden border border-[rgba(240,240,240,1)] rounded">
            <div className="flex justify-between items-center px-4 py-3 border-b border-[rgba(240,240,240,1)] bg-white shrink-0">
                <p className="text-sm font-semibold text-[#140b1d]">{constraintsSection}</p>
                <ConstraintSelector
                    items={kpiList}
                    selectedNames={selectedConstraintNames}
                    onApply={onKPISelectHandler}
                    disabled={cannotSelect}
                />
            </div>

            <div className="flex-1 min-h-0 overflow-hidden px-4 pb-3 pt-3">
                {errorExists ? (
                    <div
                        className="flex justify-center items-center h-full text-red-600"
                        data-testid={TPO_OPTIMIZE_CONSTRAINTS_ERROR}
                    >
                        {tableError}
                    </div>
                ) : groups.length ? (
                    <ConstraintsTable
                        columns={tableHeaders}
                        groups={groups}
                        labels={level}
                        onAddLevel={onAddLevel}
                        onRemoveLevel={onRemoveLevel}
                        onRemoveAttribute={onRemoveAttribute}
                        onInputChange={onInputChange}
                    />
                ) : (
                    <div
                        className="flex justify-center items-center h-full text-[rgba(0,0,0,0.25)]"
                        data-testid={tpoPartTestId(TPO_OPTIMIZE_CONSTRAINTS_TABLE, 'empty')}
                    >
                        {emptyMessage}
                    </div>
                )}
            </div>
        </div>
    );
};

ConstraintsTab.displayName = 'ConstraintsTab';
export default React.memo(ConstraintsTab);
